import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/shared/components/ui/sonner';
import { MainLayout } from '@/shared/components/layout/MainLayout';
import { AuthProvider, useAuth } from '@/shared/contexts/AuthContext';
import { TenantProvider } from '@/shared/contexts/TenantContext';
import { PermissionProvider } from '@/shared/contexts/PermissionContext';
import { PushNotificationProvider } from '@/shared/contexts/PushNotificationContext';
import { LanguageProvider } from '@/shared/contexts/LanguageContext';
import { BusinessSelectorModal } from '@/shared/components/BusinessSelector';
import '@/i18n'; // Initialize i18n
import LoginPage from '@/pages/LoginPage';
import ContextSelectionPage from '@/pages/ContextSelectionPage';
import { ERPDashboard } from '@/core/dashboard/ERPDashboard';
import { AccessDeniedPage } from '@/shared/components/auth/PermissionGate';
// Mobile Module (Warehouse) - Legacy Pages
import { MobileProvider } from '@/modules/mobile/context/MobileContext';
import MobileLayout from '@/modules/mobile/layouts/MobileLayout';
import MobileDashboard from '@/modules/mobile/pages/MobileDashboard';
import MobileReceiving from '@/modules/mobile/pages/MobileReceiving';
import MobilePutaway from '@/modules/mobile/pages/MobilePutaway';
import MobilePicking from '@/modules/mobile/pages/MobilePicking';
import MobilePacking from '@/modules/mobile/pages/MobilePacking';
import MobileLoadout from '@/modules/mobile/pages/MobileLoadout';
// Mobile Module (Warehouse) - SOTA Pages
import { WarehouseThemeProvider } from '@/modules/mobile/context/WarehouseThemeContext';
import WarehouseDashboard from '@/modules/mobile/pages/WarehouseDashboard';
import WarehouseReceiving from '@/modules/mobile/pages/WarehouseReceiving';
import WarehousePicking from '@/modules/mobile/pages/WarehousePicking';
import MobileScan from '@/modules/mobile/pages/MobileScan';
// Driver Module (EzParcel Mobile) - Legacy pages
import { DriverProvider } from '@/modules/driver/context/DriverContext';
import DriverLayout from '@/modules/driver/layouts/DriverLayout';
import DriverDashboard from '@/modules/driver/pages/DriverDashboard';
import RouteLoad from '@/modules/driver/pages/RouteLoad';
import RouteActive from '@/modules/driver/pages/RouteActive';
import StopDeliver from '@/modules/driver/pages/StopDeliver';
import StopFailed from '@/modules/driver/pages/StopFailed';
import RouteComplete from '@/modules/driver/pages/RouteComplete';
import DriverReturns from '@/modules/driver/pages/DriverReturns';
// Driver Module - SOTA (State of the Art) Pages
import { MobileThemeProvider } from '@/modules/driver/context/MobileThemeContext';
import DriverHomeDashboard from '@/modules/driver/pages/DriverHomeDashboard';
import DriverOrdersPage from '@/modules/driver/pages/DriverOrdersPage';
import DriverEarningsPage from '@/modules/driver/pages/DriverEarningsPage';
import DriverRoutePage from '@/modules/driver/pages/DriverRoutePage';
import DriverPODCapture from '@/modules/driver/pages/DriverPODCapture';
import DriverProfilePage from '@/modules/driver/pages/DriverProfilePage';
import DriverDeliverySuccess from '@/modules/driver/pages/DriverDeliverySuccess';
// EzParcel Module
import { Dashboard } from '@/modules/ezparcel/pages/Dashboard';
import { Shipments } from '@/modules/ezparcel/pages/Shipments';
import { ShipmentDetail } from '@/modules/ezparcel/pages/ShipmentDetail';
import { Routes as RoutesPage } from '@/modules/ezparcel/pages/Routes';
import { Drivers } from '@/modules/ezparcel/pages/Drivers';
import { Vehicles } from '@/modules/ezparcel/pages/Vehicles';
import { Warehouses } from '@/modules/ezparcel/pages/Warehouses';
import { Customers } from '@/modules/ezparcel/pages/Customers';
import { Billing } from '@/modules/ezparcel/pages/Billing';
import { Reports } from '@/modules/ezparcel/pages/Reports';
import { Documents } from '@/modules/ezparcel/pages/Documents';
import { Notifications } from '@/modules/ezparcel/pages/Notifications';
import { Admin } from '@/modules/ezparcel/pages/Admin';
import { Settings } from '@/modules/ezparcel/pages/Settings';
import { IntegrationsCenter } from '@/modules/ezparcel/pages/IntegrationsCenter';
import { NotificationRules } from '@/modules/ezparcel/pages/communications/NotificationRules';
import { TemplatesManager } from '@/modules/ezparcel/pages/communications/TemplatesManager';
import { MessageLogs } from '@/modules/ezparcel/pages/communications/MessageLogs';
// WMS Module
import { WMSDashboard } from '@/modules/wms/pages/WMSDashboard';
import { Inventory } from '@/modules/wms/pages/Inventory';
import { PharmaScan } from '@/modules/wms/pages/PharmaScan';
import { Products } from '@/modules/wms/pages/Products';
import { Receiving } from '@/modules/wms/pages/Receiving';
import { Putaway } from '@/modules/wms/pages/Putaway';
import { Picking } from '@/modules/wms/pages/Picking';
import { Packing } from '@/modules/wms/pages/Packing';
import { Pallets } from '@/modules/wms/pages/Pallets';
import { Expedition } from '@/modules/wms/pages/Expedition';
import { InventoryAdjustments } from '@/modules/wms/pages/InventoryAdjustments';
import { Returns } from '@/modules/wms/pages/Returns';
import { WarehouseConfig } from '@/modules/wms/pages/WarehouseConfig';
import { WMSReports } from '@/modules/wms/pages/WMSReports';
import { TransferOrders } from '@/modules/wms/pages/TransferOrders';
import { RushOrders } from '@/modules/wms/pages/RushOrders';
import { BarcodeScannerPage } from '@/modules/wms/pages/BarcodeScannerPage';
import { DeviceManagement } from '@/modules/wms/pages/DeviceManagement';
import { GPSTracking } from '@/modules/wms/pages/GPSTracking';
import { TemperatureAlerts } from '@/modules/wms/pages/TemperatureAlerts';
import Geofencing from '@/modules/wms/pages/Geofencing';
import ColdChain from '@/modules/wms/pages/ColdChain';
import NotificationSettings from '@/modules/wms/pages/NotificationSettings';
import PrintWorkflow from '@/modules/wms/pages/PrintWorkflow';
import DocumentVault from '@/modules/wms/pages/DocumentVault';
import RouteOptimization from '@/modules/wms/pages/RouteOptimization';
// Trail Visualization
import TrailDashboard from '@/modules/wms/pages/trails/TrailDashboard';
import AuditTrail from '@/modules/wms/pages/trails/AuditTrail';
import OperationalTrail from '@/modules/wms/pages/trails/OperationalTrail';
import ScanTrail from '@/modules/wms/pages/trails/ScanTrail';
import ProofTrail from '@/modules/wms/pages/trails/ProofTrail';
// Admin Module
import AccessProfiles from '@/modules/admin/pages/AccessProfiles';
import SystemSettings from '@/modules/admin/pages/SystemSettings';
import APIKeyManagement from '@/modules/admin/pages/APIKeyManagement';
// User Settings (i18n)
import UserSettings from '@/core/settings/UserSettings';
// Finance Module
import { FinanceDashboard } from '@/modules/finance/pages/FinanceDashboard';
import { GeneralLedger } from '@/modules/finance/pages/GeneralLedger';
import { AccountsPayable } from '@/modules/finance/pages/AccountsPayable';
import { AccountsReceivable } from '@/modules/finance/pages/AccountsReceivable';
import { FixedAssets } from '@/modules/finance/pages/FixedAssets';
import { TaxManagement } from '@/modules/finance/pages/TaxManagement';
import { Banking } from '@/modules/finance/pages/Banking';
import { FinancialReports } from '@/modules/finance/pages/FinancialReports';
import { ExpenseClaims } from '@/modules/finance/pages/ExpenseClaims';
import { BudgetManagement } from '@/modules/finance/pages/BudgetManagement';
import { DocumentSetup } from '@/modules/finance/pages/DocumentSetup';
import { BankFeedsIntegration } from '@/modules/finance/pages/BankFeedsIntegration';
// Ecommerce Module
import { EcommerceDashboard } from '@/modules/ecommerce/pages/EcommerceDashboard';
import { StorefrontBuilder } from '@/modules/ecommerce/pages/StorefrontBuilder';
import { EcommerceStores } from '@/modules/ecommerce/pages/EcommerceStores';
import { EcommerceOrders } from '@/modules/ecommerce/pages/EcommerceOrders';
import { EcommerceProducts } from '@/modules/ecommerce/pages/EcommerceProducts';
import { StoreEditor } from '@/modules/ecommerce/pages/StoreEditor';
import { PublicStorefront } from '@/modules/ecommerce/pages/PublicStorefront';
// Social Media CNS Module
import { CNSDashboard } from '@/modules/cns/pages/CNSDashboard';
import { ContentStudio } from '@/modules/cns/pages/ContentStudio';
import { Scheduler } from '@/modules/cns/pages/Scheduler';
import { Channels } from '@/modules/cns/pages/Channels';
import { Campaigns as CNSCampaigns } from '@/modules/cns/pages/Campaigns';
import { Analytics } from '@/modules/cns/pages/Analytics';
import { EmailMarketing } from '@/modules/cns/pages/EmailMarketing';
import { AdsManager } from '@/modules/cns/pages/AdsManager';
import { CNSSettings } from '@/modules/cns/pages/CNSSettings';
import { AISettings } from '@/modules/cns/pages/AISettings';
// Report Builder Module
import { ReportBuilder, ReportEditor, ReportPreview, ReportTypes } from '@/modules/reports/pages';
// Procurement Module
import { ProcurementDashboard } from '@/modules/procurement/pages/ProcurementDashboard';
import { Vendors } from '@/modules/procurement/pages/Vendors';
import { PurchaseOrders } from '@/modules/procurement/pages/PurchaseOrders';
import { RFQManagement } from '@/modules/procurement/pages/RFQManagement';
import { GoodsReceipt } from '@/modules/procurement/pages/GoodsReceipt';
import { VendorPayments } from '@/modules/procurement/pages/VendorPayments';
import { ProcurementReports } from '@/modules/procurement/pages/ProcurementReports';
import { PurchaseRequisitions } from '@/modules/procurement/pages/PurchaseRequisitions';
import { SupplierReturns } from '@/modules/procurement/pages/SupplierReturns';
// CRM Module
import { CRMDashboard } from '@/modules/crm/pages/CRMDashboard';
import { Leads } from '@/modules/crm/pages/Leads';
import { Pipeline } from '@/modules/crm/pages/Pipeline';
import { Opportunities } from '@/modules/crm/pages/Opportunities';
import { Quotes } from '@/modules/crm/pages/Quotes';
import { Accounts } from '@/modules/crm/pages/Accounts';
import { Activities } from '@/modules/crm/pages/Activities';
import { CRMReports } from '@/modules/crm/pages/CRMReports';
import { DropshipOrders } from '@/modules/crm/pages/DropshipOrders';
import { ConsignmentOrders } from '@/modules/crm/pages/ConsignmentOrders';
import { ProspectDiscovery } from '@/modules/crm/pages/ProspectDiscovery';
import { CampaignIntelligence } from '@/modules/crm/pages/CampaignIntelligence';
import PortalManager from '@/modules/crm/pages/PortalManager';
import PublishingConsole from '@/modules/crm/pages/PublishingConsole';
import AdConnectors from '@/modules/crm/pages/AdConnectors';
import { SalesMarketingHub } from '@/modules/crm/pages/SalesMarketingHub';
import { DocumentPreview } from '@/modules/crm/pages/DocumentPreview';
import OrchestrationCenter from '@/modules/crm/pages/OrchestrationCenter';
import PresentationHub from '@/modules/crm/pages/PresentationHub';
import PresentationHubEditor from '@/modules/crm/pages/PresentationHubEditor';
import PublicPresentationPage from '@/modules/crm/presentation-hub/PublicPresentationPage';
import PublicPortalPage from '@/modules/crm/presentation-hub/PublicPortalPage';
import { PRESENTATION_HUB_PUBLIC_ROUTES } from '@/modules/crm/presentation-hub/presentationHubRoutes';
// Communications Module
import { CommunicationsDashboard } from '@/modules/communications/pages/CommunicationsDashboard';
import { TelegramSettings } from '@/modules/communications/pages/TelegramSettings';
import { WhatsAppSettings } from '@/modules/communications/pages/WhatsAppSettings';
import { SMSSettings } from '@/modules/communications/pages/SMSSettings';
import { EmailSettings } from '@/modules/communications/pages/EmailSettings';
import { MessageTemplates } from '@/modules/communications/pages/MessageTemplates';
import { NotificationRules as CommNotificationRules } from '@/modules/communications/pages/NotificationRules';
import { DeliveryLogs } from '@/modules/communications/pages/DeliveryLogs';
// OIG Module
import { OIGDashboard } from '@/modules/oig/pages/OIGDashboard';
import { NFeConfiguration } from '@/modules/oig/pages/NFeConfiguration';
import { NFeList } from '@/modules/oig/pages/NFeList';
import { NFeUpload } from '@/modules/oig/pages/NFeUpload';
import { CTeList } from '@/modules/oig/pages/CTeList';
import { EDIPartners } from '@/modules/oig/pages/EDIPartners';
import { CSVImport } from '@/modules/oig/pages/CSVImport';
import { ImportHistory } from '@/modules/oig/pages/ImportHistory';
import { ASNManagement } from '@/modules/oig/pages/ASNManagement';
// Cold Chain Pages (embedded in EzParcel)
import { ColdChainDashboard } from '@/modules/ezparcel/pages/coldchain/ColdChainDashboard';
import { SensorManagement } from '@/modules/ezparcel/pages/coldchain/SensorManagement';
import { ExcursionManagement } from '@/modules/ezparcel/pages/coldchain/ExcursionManagement';
import { ColdChainRules } from '@/modules/ezparcel/pages/coldchain/ColdChainRules';
import { SensorBinding } from '@/modules/ezparcel/pages/coldchain/SensorBinding';
// Trails Pages (embedded in EzParcel)
import { TrailsDashboard } from '@/modules/ezparcel/pages/trails/TrailsDashboard';
import { RouteReplay } from '@/modules/ezparcel/pages/trails/RouteReplay';
// Calendar Module
import CalendarPage from '@/modules/calendar/pages/CalendarPage';
import EventRulesPage from '@/modules/calendar/pages/EventRulesPage';
import ExternalSyncPage from '@/modules/calendar/pages/ExternalSyncPage';
import HolidayCalendarPage from '@/modules/calendar/pages/HolidayCalendarPage';
import WorkingHoursPage from '@/modules/calendar/pages/WorkingHoursPage';
// Admin Module
import HelpCenter from '@/modules/admin/pages/HelpCenter';
// EzTask Module
import { EzTaskProvider } from '@/modules/eztask/context/EzTaskContext';
import BoardList from '@/modules/eztask/pages/BoardList';
import BoardView from '@/modules/eztask/pages/BoardView';
import EzTaskDashboard from '@/modules/eztask/pages/Dashboard';
import AutomationBuilder from '@/modules/eztask/pages/AutomationBuilder';
// HR Module
import { HRDashboard, EmployeesPage, TimeAttendance, LeaveManagement, PayrollPage, PayComponentsPage, ShiftsPage, PositionsPage } from '@/modules/hr';
// Accounting Module
import { AccountingDashboard, ChartOfAccounts, JournalEntries, EventInbox, PeriodClose, PostingRulesPage, StatutoryExportsPage } from '@/modules/accounting';
// Fiscal Module
import { FiscalDashboard, FiscalDocumentsPage, FiscalApuracaoPage, FiscalSpedPage, FiscalMatrixPage, FiscalEventsPage, FiscalAuditEvidencePage } from '@/modules/fiscal';
// Finance Module - New Pages
import { ARInvoicesPage } from '@/modules/finance/pages/ARInvoicesPage';
import { APBillsPage } from '@/modules/finance/pages/APBillsPage';
import { PaymentsPage } from '@/modules/finance/pages/PaymentsPage';
// Landing Page Setup Module
import { LandingSetupDashboard, SectionEditor, BrandingSettings, VersionHistory, TranslationsManager, SkinManager } from '@/modules/landing-setup';
// Fleet Management Module
import { FleetDashboard, VehiclesPage as FleetVehiclesPage, DriversPage as FleetDriversPage, TripDetailPage, PartsManagementPage, FleetProcurementPage, TireManagementPage, FuelLogsPage, LiveTrackingPage, MaintenanceWorkOrdersPage, DriverMobileAppPage, RouteOptimizationPage, TelematicsPage, FleetAnalyticsPage } from '@/modules/fleet-management';
import { CMSEnhancementsPage } from '@/modules/landing-page';
// Public Landing Page
import PublicLandingPage from '@/pages/public/LandingPage';
import { ModuleDetailPage } from '@/pages/public/ModuleDetailPage';

import '@/App.css';

// Hook to detect mobile device
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileDevice = mobileRegex.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Root always shows the public Landing Page — auth state does not matter.
// Authenticated users access the ERP via /erp (not via root).
const SmartRoot = () => <PublicLandingPage />;


// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route wrapper — bounces already-authenticated users to /erp
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/erp" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Smart Root — Landing Page for visitors, Dashboard for authenticated users */}
      <Route path="/" element={<SmartRoot />} />

      {/* Public Routes — /login bounces authenticated users to /erp */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/login/secret" element={<PublicRoute><LoginPage showDemoCredentials /></PublicRoute>} />
      <Route path="/select-context" element={<ProtectedRoute><ContextSelectionPage /></ProtectedRoute>} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />

      {/* Public Landing Page */}
      <Route path="/landing" element={<PublicLandingPage />} />
      <Route path="/landing-preview" element={<PublicLandingPage />} />
      <Route path="/modules/:moduleSlug" element={<ModuleDetailPage />} />
      <Route path="/ezerp-full-presentation" element={<PublicPresentationPage />} />
      <Route path="/ezerp-partner-presentation" element={<PublicPresentationPage />} />
      <Route path="/emissor-presentation" element={<PublicPresentationPage />} />
      <Route path="/brazilian-rewards-presentation" element={<PublicPresentationPage />} />
      <Route path="/full-stack-fiscal-platform" element={<PublicPresentationPage />} />
      <Route path="/process-map-presentation" element={<PublicPresentationPage />} />
      <Route path="/presentation/:presentationSlug" element={<PublicPresentationPage />} />
      <Route path="/portal/:slug" element={<PublicPortalPage />} />

      {/* Public Storefront Routes - Customer-facing e-commerce */}
      <Route path="/store/:storeSlug" element={<PublicStorefront />} />

      {/* Mobile PWA Routes - SOTA EzWarehouse Mobile for operators */}
      <Route path="/mobile/*" element={
        <ProtectedRoute>
          <MobileProvider>
            <WarehouseThemeProvider>
              <Routes>
                {/* SOTA Pages - New Award-Winning UI */}
                <Route path="/" element={<WarehouseDashboard />} />
                <Route path="/receiving" element={<WarehouseReceiving />} />
                <Route path="/picking" element={<WarehousePicking />} />
                <Route path="/scan" element={<MobileScan />} />
                {/* Legacy Pages - Still accessible */}
                <Route path="/legacy" element={<MobileLayout><MobileDashboard /></MobileLayout>} />
                <Route path="/putaway" element={<MobileLayout><MobilePutaway /></MobileLayout>} />
                <Route path="/packing" element={<MobileLayout><MobilePacking /></MobileLayout>} />
                <Route path="/loadout" element={<MobileLayout><MobileLoadout /></MobileLayout>} />
              </Routes>
            </WarehouseThemeProvider>
          </MobileProvider>
        </ProtectedRoute>
      } />

      {/* Driver PWA Routes - SOTA EzParcel Mobile for drivers */}
      <Route path="/driver/*" element={
        <ProtectedRoute>
          <MobileProvider>
            <DriverProvider>
              <MobileThemeProvider>
                <Routes>
                  {/* SOTA Pages - New Award-Winning UI */}
                  <Route path="/" element={<DriverHomeDashboard />} />
                  <Route path="/dashboard" element={<DriverHomeDashboard />} />
                  <Route path="/orders" element={<DriverOrdersPage />} />
                  <Route path="/earnings" element={<DriverEarningsPage />} />
                  <Route path="/route" element={<DriverRoutePage />} />
                  <Route path="/pod" element={<DriverPODCapture />} />
                  <Route path="/profile" element={<DriverProfilePage />} />
                  <Route path="/success" element={<DriverDeliverySuccess />} />
                  {/* Legacy Pages - Still accessible */}
                  <Route path="/legacy" element={<DriverLayout><DriverDashboard /></DriverLayout>} />
                  <Route path="/route-load" element={<DriverLayout><RouteLoad /></DriverLayout>} />
                  <Route path="/route-active" element={<DriverLayout><RouteActive /></DriverLayout>} />
                  <Route path="/stop-deliver" element={<DriverLayout><StopDeliver /></DriverLayout>} />
                  <Route path="/stop-failed" element={<DriverLayout><StopFailed /></DriverLayout>} />
                  <Route path="/route-complete" element={<DriverLayout><RouteComplete /></DriverLayout>} />
                  <Route path="/returns" element={<DriverLayout><DriverReturns /></DriverLayout>} />
                </Routes>
              </MobileThemeProvider>
            </DriverProvider>
          </MobileProvider>
        </ProtectedRoute>
      } />

      {/* Protected Routes */}
      <Route path="/*" element={
        <ProtectedRoute>
          <MainLayout>
            <Routes>
              {/* ERP Main Dashboard - shows all modules */}
              <Route path="/" element={<ERPDashboard />} />
              <Route path="/erp" element={<ERPDashboard />} />

              {/* EzParcel Module Routes */}
              <Route path="/ezparcel" element={<Navigate to="/ezparcel/dashboard" replace />} />
              <Route path="/ezparcel/dashboard" element={<Dashboard />} />
              <Route path="/ezparcel/fleet" element={<FleetDashboard />} />
              <Route path="/ezparcel/fleet/vehicles" element={<FleetVehiclesPage />} />
              <Route path="/ezparcel/fleet/drivers" element={<FleetDriversPage />} />
              <Route path="/ezparcel/fleet/trips/:tripId" element={<TripDetailPage />} />
              <Route path="/ezparcel/fleet/parts" element={<PartsManagementPage />} />
              <Route path="/ezparcel/fleet/procurement" element={<FleetProcurementPage />} />
              <Route path="/ezparcel/fleet/tires" element={<TireManagementPage />} />
              <Route path="/ezparcel/fleet/fuel" element={<FuelLogsPage />} />
              <Route path="/ezparcel/fleet/maintenance" element={<MaintenanceWorkOrdersPage />} />
              <Route path="/ezparcel/fleet/tracking" element={<LiveTrackingPage />} />
              <Route path="/ezparcel/fleet/driver-mobile" element={<DriverMobileAppPage />} />
              <Route path="/ezparcel/fleet/route-optimization" element={<RouteOptimizationPage />} />
              <Route path="/ezparcel/fleet/telematics" element={<TelematicsPage />} />
              <Route path="/ezparcel/fleet/analytics" element={<FleetAnalyticsPage />} />
              <Route path="/ezparcel/fleet/alerts" element={<FleetDashboard />} />
              <Route path="/ezparcel/cms-enhancements" element={<CMSEnhancementsPage />} />
              <Route path="/ezparcel/shipments" element={<Shipments />} />
              <Route path="/ezparcel/shipments/:id" element={<ShipmentDetail />} />
              <Route path="/ezparcel/routes" element={<RoutesPage />} />
              <Route path="/ezparcel/drivers" element={<Drivers />} />
              <Route path="/ezparcel/vehicles" element={<Vehicles />} />
              <Route path="/ezparcel/warehouses" element={<Warehouses />} />
              <Route path="/ezparcel/customers" element={<Customers />} />
              <Route path="/ezparcel/billing" element={<Billing />} />
              <Route path="/ezparcel/reports" element={<Reports />} />
              <Route path="/ezparcel/documents" element={<Documents />} />
              <Route path="/ezparcel/documents" element={<Documents />} />
              <Route path="/ezparcel/notifications" element={<Notifications />} />
              <Route path="/ezparcel/admin" element={<Admin />} />
              <Route path="/ezparcel/settings" element={<Settings />} />
              <Route path="/ezparcel/integrations" element={<IntegrationsCenter />} />
              <Route path="/ezparcel/communications/rules" element={<NotificationRules />} />
              <Route path="/ezparcel/communications/templates" element={<TemplatesManager />} />
              <Route path="/ezparcel/communications/logs" element={<MessageLogs />} />

              {/* Cold Chain Routes (embedded in EzParcel) */}
              <Route path="/ezparcel/coldchain" element={<ColdChainDashboard />} />
              <Route path="/ezparcel/coldchain/dashboard" element={<ColdChainDashboard />} />
              <Route path="/ezparcel/coldchain/sensors" element={<SensorManagement />} />
              <Route path="/ezparcel/coldchain/excursions" element={<ExcursionManagement />} />
              <Route path="/ezparcel/coldchain/rules" element={<ColdChainRules />} />
              <Route path="/ezparcel/coldchain/binding" element={<SensorBinding />} />

              {/* Trails Routes (embedded in EzParcel) */}
              <Route path="/ezparcel/trails" element={<TrailsDashboard />} />
              <Route path="/ezparcel/trails/dashboard" element={<TrailsDashboard />} />
              <Route path="/ezparcel/trails/replay" element={<RouteReplay />} />

              {/* Legacy routes - redirect to new structure */}
              <Route path="/shipments" element={<Shipments />} />
              <Route path="/shipments/:id" element={<ShipmentDetail />} />
              <Route path="/routes" element={<RoutesPage />} />
              <Route path="/drivers" element={<Drivers />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/warehouses" element={<Warehouses />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/integrations" element={<IntegrationsCenter />} />
              <Route path="/profile" element={<Navigate to="/settings/user" replace />} />
              <Route path="/user/profile" element={<Navigate to="/settings/user" replace />} />

              {/* WMS Module Routes */}
              <Route path="/wms" element={<WMSDashboard />} />
              <Route path="/wms/dashboard" element={<WMSDashboard />} />
              <Route path="/wms/inventory" element={<Inventory />} />
              <Route path="/wms/pharmascan" element={<PharmaScan />} />
              <Route path="/wms/products" element={<Products />} />
              <Route path="/wms/receiving" element={<Receiving />} />
              <Route path="/wms/putaway" element={<Putaway />} />
              <Route path="/wms/picking" element={<Picking />} />
              <Route path="/wms/packing" element={<Packing />} />
              <Route path="/wms/pallets" element={<Pallets />} />
              <Route path="/wms/expedition" element={<Expedition />} />
              <Route path="/wms/adjustments" element={<InventoryAdjustments />} />
              <Route path="/wms/returns" element={<Returns />} />
              <Route path="/wms/transfers" element={<TransferOrders />} />
              <Route path="/wms/rush-orders" element={<RushOrders />} />
              <Route path="/wms/scanner" element={<BarcodeScannerPage />} />
              <Route path="/wms/config" element={<WarehouseConfig />} />
              <Route path="/wms/reports" element={<WMSReports />} />
              <Route path="/wms/devices" element={<DeviceManagement />} />
              <Route path="/wms/devices/scanners" element={<DeviceManagement />} />
              <Route path="/wms/devices/printers" element={<DeviceManagement />} />
              <Route path="/wms/devices/mobile" element={<DeviceManagement />} />
              <Route path="/wms/devices/gps" element={<DeviceManagement />} />
              <Route path="/wms/devices/sensors" element={<DeviceManagement />} />
              <Route path="/wms/gps-tracking" element={<GPSTracking />} />
              <Route path="/wms/temperature-alerts" element={<TemperatureAlerts />} />
              <Route path="/wms/geofencing" element={<Geofencing />} />
              <Route path="/wms/coldchain" element={<ColdChain />} />
              <Route path="/wms/notifications" element={<NotificationSettings />} />
              <Route path="/wms/print" element={<PrintWorkflow />} />
              <Route path="/wms/documents" element={<DocumentVault />} />
              <Route path="/wms/route-optimization" element={<RouteOptimization />} />

              {/* Trail Visualization Routes */}
              <Route path="/wms/trails" element={<TrailDashboard />} />
              <Route path="/wms/trails/audit" element={<AuditTrail />} />
              <Route path="/wms/trails/operational" element={<OperationalTrail />} />
              <Route path="/wms/trails/scan" element={<ScanTrail />} />
              <Route path="/wms/trails/proof" element={<ProofTrail />} />

              {/* Admin Module Routes */}
              <Route path="/admin/access-profiles" element={<AccessProfiles />} />
              <Route path="/admin/system-settings" element={<SystemSettings />} />
              <Route path="/admin/api-keys" element={<APIKeyManagement />} />

              {/* User Settings Route (i18n) */}
              <Route path="/settings/user" element={<UserSettings />} />

              {/* Finance Module Routes */}
              <Route path="/finance" element={<FinanceDashboard />} />
              <Route path="/finance/dashboard" element={<FinanceDashboard />} />
              <Route path="/finance/general-ledger" element={<GeneralLedger />} />
              <Route path="/finance/accounts-payable" element={<AccountsPayable />} />
              <Route path="/finance/accounts-receivable" element={<AccountsReceivable />} />
              <Route path="/finance/fixed-assets" element={<FixedAssets />} />
              <Route path="/finance/tax" element={<TaxManagement />} />
              <Route path="/finance/banking" element={<Banking />} />
              <Route path="/finance/reports" element={<FinancialReports />} />
              <Route path="/finance/expenses" element={<ExpenseClaims />} />
              <Route path="/finance/budgets" element={<BudgetManagement />} />
              <Route path="/finance/document-setup" element={<DocumentSetup />} />
              <Route path="/finance/bank-feeds" element={<BankFeedsIntegration />} />

              {/* Ecommerce Module Routes */}
              <Route path="/ecommerce" element={<EcommerceDashboard />} />
              <Route path="/ecommerce/dashboard" element={<EcommerceDashboard />} />
              <Route path="/ecommerce/builder" element={<StorefrontBuilder />} />
              <Route path="/ecommerce/stores" element={<EcommerceStores />} />
              <Route path="/ecommerce/orders" element={<EcommerceOrders />} />
              <Route path="/ecommerce/products" element={<EcommerceProducts />} />
              <Route path="/ecommerce/stores/:storeId/edit" element={<StoreEditor />} />

              {/* Procurement Module Routes */}
              <Route path="/procurement" element={<ProcurementDashboard />} />
              <Route path="/procurement/dashboard" element={<ProcurementDashboard />} />
              <Route path="/procurement/vendors" element={<Vendors />} />
              <Route path="/procurement/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/procurement/requisitions" element={<PurchaseRequisitions />} />
              <Route path="/procurement/rfq" element={<RFQManagement />} />
              <Route path="/procurement/goods-receipt" element={<GoodsReceipt />} />
              <Route path="/procurement/supplier-returns" element={<SupplierReturns />} />
              <Route path="/procurement/payments" element={<VendorPayments />} />
              <Route path="/procurement/reports" element={<ProcurementReports />} />

              {/* Social Media CNS Module Routes */}
              <Route path="/cns" element={<CNSDashboard />} />
              <Route path="/cns/dashboard" element={<CNSDashboard />} />
              <Route path="/cns/content" element={<ContentStudio />} />
              <Route path="/cns/content/new" element={<ContentStudio />} />
              <Route path="/cns/scheduler" element={<Scheduler />} />
              <Route path="/cns/calendar" element={<Scheduler />} />
              <Route path="/cns/channels" element={<Channels />} />
              <Route path="/cns/campaigns" element={<CNSCampaigns />} />
              <Route path="/cns/analytics" element={<Analytics />} />
              <Route path="/cns/email" element={<EmailMarketing />} />
              <Route path="/cns/ads" element={<AdsManager />} />
              <Route path="/cns/settings" element={<CNSSettings />} />
              <Route path="/cns/ai" element={<AISettings />} />

              {/* Report Builder Module Routes */}
              <Route path="/reports" element={<ReportBuilder />} />
              <Route path="/reports/new" element={<ReportEditor />} />
              <Route path="/reports/edit/:id" element={<ReportEditor />} />
              <Route path="/reports/preview/:id" element={<ReportPreview />} />
              <Route path="/reports/types" element={<ReportTypes />} />
              <Route path="/reports/permissions" element={<ReportTypes />} />
              <Route path="/reports/schedules" element={<ReportBuilder />} />
              <Route path="/reports/schedule/:id" element={<ReportEditor />} />

              {/* CRM Module Routes */}
              <Route path="/crm" element={<CRMDashboard />} />
              <Route path="/crm/dashboard" element={<CRMDashboard />} />
              <Route path="/crm/leads" element={<Leads />} />
              <Route path="/crm/pipeline" element={<Pipeline />} />
              <Route path="/crm/opportunities" element={<Opportunities />} />
              <Route path="/crm/quotes" element={<Quotes />} />
              <Route path="/crm/accounts" element={<Accounts />} />
              <Route path="/crm/activities" element={<Activities />} />
              <Route path="/crm/dropship" element={<DropshipOrders />} />
              <Route path="/crm/consignment" element={<ConsignmentOrders />} />
              <Route path="/crm/reports" element={<CRMReports />} />
              <Route path="/crm/prospect-discovery" element={<ProspectDiscovery />} />
              <Route path="/crm/campaign-intelligence" element={<CampaignIntelligence />} />
              <Route path="/crm/marketing-hub" element={<SalesMarketingHub />} />
              <Route path="/crm/marketing-hub/preview/:id" element={<DocumentPreview />} />
              <Route path="/crm/portal-manager" element={<PortalManager />} />
              <Route path="/crm/orchestration" element={<OrchestrationCenter />} />
              <Route path="/crm/publishing-console" element={<PublishingConsole />} />
              <Route path="/crm/ad-connectors" element={<AdConnectors />} />
              <Route path="/crm/presentation-hub" element={<PresentationHub />} />
              <Route path="/crm/presentation-hub/editor/:deckId" element={<PresentationHubEditor />} />

              {/* Communications Module Routes */}
              <Route path="/communications" element={<CommunicationsDashboard />} />
              <Route path="/communications/dashboard" element={<CommunicationsDashboard />} />
              <Route path="/communications/telegram" element={<TelegramSettings />} />
              <Route path="/communications/whatsapp" element={<WhatsAppSettings />} />
              <Route path="/communications/sms" element={<SMSSettings />} />
              <Route path="/communications/email" element={<EmailSettings />} />
              <Route path="/communications/templates" element={<MessageTemplates />} />
              <Route path="/communications/rules" element={<CommNotificationRules />} />
              <Route path="/communications/logs" element={<DeliveryLogs />} />

              {/* OIG Module Routes */}
              <Route path="/oig" element={<OIGDashboard />} />
              <Route path="/oig/dashboard" element={<OIGDashboard />} />
              <Route path="/oig/nfe/config" element={<NFeConfiguration />} />
              <Route path="/oig/nfe" element={<NFeList />} />
              <Route path="/oig/nfe/upload" element={<NFeUpload />} />
              <Route path="/oig/cte" element={<CTeList />} />
              <Route path="/oig/edi" element={<EDIPartners />} />
              <Route path="/oig/csv" element={<CSVImport />} />
              <Route path="/oig/asn" element={<ASNManagement />} />
              <Route path="/oig/history" element={<ImportHistory />} />

              {/* Calendar Module Routes */}
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/calendar/events" element={<CalendarPage />} />
              <Route path="/calendar/rules" element={<EventRulesPage />} />
              <Route path="/calendar/sync" element={<ExternalSyncPage />} />
              <Route path="/calendar/holidays" element={<HolidayCalendarPage />} />
              <Route path="/calendar/working-hours" element={<WorkingHoursPage />} />

              {/* EzTask Module Routes */}
              <Route path="/eztask" element={<EzTaskProvider><BoardList /></EzTaskProvider>} />
              <Route path="/eztask/boards" element={<EzTaskProvider><BoardList /></EzTaskProvider>} />
              <Route path="/eztask/board/:boardId" element={<EzTaskProvider><BoardView /></EzTaskProvider>} />
              <Route path="/eztask/board/:boardId/automations" element={<EzTaskProvider><AutomationBuilder /></EzTaskProvider>} />
              <Route path="/eztask/dashboard" element={<EzTaskProvider><EzTaskDashboard /></EzTaskProvider>} />

              {/* Landing Page Setup Module Routes */}
              <Route path="/admin/landing" element={<LandingSetupDashboard />} />
              <Route path="/admin/landing/sections" element={<SectionEditor />} />
              <Route path="/admin/landing/branding" element={<BrandingSettings />} />
              <Route path="/admin/landing/skins" element={<SkinManager />} />
              <Route path="/admin/landing/versions" element={<VersionHistory />} />
              <Route path="/admin/landing/translations" element={<TranslationsManager />} />

              {/* Admin / Help Center */}
              <Route path="/admin/help" element={<HelpCenter />} />
              <Route path="/admin/help-center" element={<HelpCenter />} />
              <Route path="/help" element={<HelpCenter />} />

              {/* WMS Admin Route Aliases (per spec requirement: both /wms/admin/* AND /admin/*) */}
              <Route path="/wms/admin/access-profiles" element={<AccessProfiles />} />
              <Route path="/wms/admin/system-settings" element={<SystemSettings />} />
              <Route path="/wms/admin/api-keys" element={<APIKeyManagement />} />
              <Route path="/wms/admin/help" element={<HelpCenter />} />

              {/* HR Module Routes */}
              <Route path="/hr" element={<HRDashboard />} />
              <Route path="/hr/dashboard" element={<HRDashboard />} />
              <Route path="/hr/employees" element={<EmployeesPage />} />
              <Route path="/hr/time-attendance" element={<TimeAttendance />} />
              <Route path="/hr/leave" element={<LeaveManagement />} />
              <Route path="/hr/payroll" element={<PayrollPage />} />
              <Route path="/hr/pay-components" element={<PayComponentsPage />} />
              <Route path="/hr/shifts" element={<ShiftsPage />} />
              <Route path="/hr/positions" element={<PositionsPage />} />

              {/* Accounting Module Routes */}
              <Route path="/accounting" element={<AccountingDashboard />} />
              <Route path="/accounting/dashboard" element={<AccountingDashboard />} />
              <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="/accounting/journals" element={<JournalEntries />} />
              <Route path="/accounting/events" element={<EventInbox />} />
              <Route path="/accounting/periods" element={<PeriodClose />} />
              <Route path="/accounting/exports" element={<StatutoryExportsPage />} />
              <Route path="/accounting/posting-rules" element={<PostingRulesPage />} />

              {/* Fiscal Module Routes */}
              <Route path="/fiscal" element={<FiscalDashboard />} />
              <Route path="/fiscal/dashboard" element={<FiscalDashboard />} />
              <Route path="/fiscal/documents" element={<FiscalDocumentsPage />} />
              <Route path="/fiscal/apuracao" element={<FiscalApuracaoPage />} />
              <Route path="/fiscal/sped" element={<FiscalSpedPage />} />
              <Route path="/fiscal/matrix" element={<FiscalMatrixPage />} />
              <Route path="/fiscal/events" element={<FiscalEventsPage />} />
              <Route path="/fiscal/audit" element={<FiscalAuditEvidencePage />} />

              {/* Finance Module - AR/AP Routes */}
              <Route path="/finance/ar/invoices" element={<ARInvoicesPage />} />
              <Route path="/finance/ap/bills" element={<APBillsPage />} />
              <Route path="/finance/payments" element={<PaymentsPage />} />

              {/* WMS Settings Route Aliases (per spec requirement: both /wms/settings/* AND /settings/*) */}
              <Route path="/wms/settings/user" element={<UserSettings />} />
              <Route path="/wms/settings/traffic" element={<SystemSettings />} />
              <Route path="/wms/settings/storage" element={<SystemSettings />} />
            </Routes>
          </MainLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function AppShell() {
  const location = useLocation();
  const isPublicPresentationRoute = PRESENTATION_HUB_PUBLIC_ROUTES.some(
    (route) => route.publicPath === location.pathname
  );

  useEffect(() => {
    window.__ezErpCapture = async (targetSelector = 'body') => {
      let renderHost = null;
      try {
        // Wait 1 frame to ensure reflows have finished
        await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
        const { default: html2canvas } = await import('html2canvas');
        const originalNode = document.querySelector(targetSelector);
        if (!originalNode) throw new Error('Target node not found for capture');
        
        // Create an unconstrained testing environment dynamically
        const clone = originalNode.cloneNode(true);
        renderHost = document.createElement('div');
        renderHost.setAttribute('aria-hidden', 'true');
        Object.assign(renderHost.style, {
          position: 'fixed',
          left: '-20000px',
          top: '0',
          width: Math.max(originalNode.scrollWidth, 1200) + 'px',
          maxWidth: 'none',
          visibility: 'visible',
          pointerEvents: 'none',
          overflow: 'visible',
          zIndex: '-1',
          opacity: '1',
          background: '#ffffff'
        });
        
        // Attempt to port all current styles broadly
        const cssTexts = [];
        for (const sheet of Array.from(document.styleSheets)) {
          try {
            for (const rule of Array.from(sheet.cssRules || [])) {
              cssTexts.push(rule.cssText);
            }
          } catch(e) {}
        }
        const styleNode = document.createElement('style');
        styleNode.textContent = cssTexts.join('\n');
        renderHost.appendChild(styleNode);
        
        renderHost.appendChild(clone);
        document.body.appendChild(renderHost);
        
        // Calculate true mathematical unconstrained dimensions
        const contentWidth = Math.max(renderHost.scrollWidth, clone.scrollWidth, 1200);
        const contentHeight = Math.max(renderHost.scrollHeight, clone.scrollHeight, 800);
        renderHost.style.width = contentWidth + 'px';
        renderHost.style.height = contentHeight + 'px';
        
        const canvas = await html2canvas(renderHost, {
          width: contentWidth,
          height: contentHeight,
          windowWidth: contentWidth,
          windowHeight: contentHeight,
          useCORS: true,
          scale: 2,
          logging: false
        });
        
        return canvas.toDataURL('image/png');
      } catch (err) {
        console.error('Full-page global capture hook failed:', err);
        throw err;
      } finally {
        if (renderHost) renderHost.remove();
      }
    };
  }, []);

  if (isPublicPresentationRoute) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      }>
        <PublicPresentationPage />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <TenantProvider>
        <PermissionProvider>
          <PushNotificationProvider>
            <LanguageProvider>
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-background">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                </div>
              }>
                <AppRoutes />
                <BusinessSelectorModal />
                <Toaster position="top-right" />
              </Suspense>
            </LanguageProvider>
          </PushNotificationProvider>
        </PermissionProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;


